import { ChangeDetectionStrategy, Component, Input } from '@angular/core';

import { DxcCountUpDirective } from '../count-up/dxc-count-up.directive';

/**
 * El color de una porción o de una serie. Sale de la capa semántica
 * (`.dxc-tone--*`). Los `-light` son el mismo matiz rebajado: dos categorías de
 * la misma familia —«Asignado» y «Completado», las dos en verde— se distinguen
 * sin salirse de su color.
 */
export type DxcChartTone =
    | 'accent' | 'brand' | 'neutral'
    | 'info' | 'info-light'
    | 'success' | 'success-light'
    | 'warning' | 'danger';

export type DxcDonutSlice = { key: string; label: string; count: number; tone: DxcChartTone };

const RADIUS = 62;
const CIRCUMFERENCE = 2 * Math.PI * RADIUS;
/** Aire entre porciones, en unidades del trazado. Sin él dos tonos vecinos se funden. */
const GAP = 3;

type DrawnSlice = DxcDonutSlice & { dash: string; offset: number; pct: number };

/**
 * Dona con su leyenda: el reparto de un total entre categorías. El total va
 * escrito en el centro, y la leyenda lleva siempre el nombre, la cifra y el
 * porcentaje, así que el color distingue pero no es la única señal (§9).
 *
 * Porción y fila de la leyenda son la misma categoría: apuntar una realza las
 * dos y pone su cifra en el centro.
 */
@Component({
    standalone: true,
    selector: 'dxc-donut',
    imports: [DxcCountUpDirective],
    template: `
        @if (total === 0) {
            <div class="dxc-donut__empty">{{ emptyText }}</div>
        } @else {
            <div class="dxc-donut" [class.dxc-donut--focus]="!!active" (mouseleave)="active = null">
                <div class="dxc-donut__figure">
                    <svg class="dxc-donut__chart" viewBox="0 0 160 160" role="img" [attr.aria-label]="ariaLabel">
                        <circle class="dxc-donut__track" cx="80" cy="80" [attr.r]="radius" fill="none"></circle>
                        @for (slice of drawn; track slice.key; let i = $index) {
                            <circle class="dxc-donut__slice dxc-tone--{{ slice.tone }}" cx="80" cy="80" [attr.r]="radius" fill="none"
                                [class.is-active]="active === slice.key" [style.--dxc-donut-i]="i"
                                [attr.stroke-dasharray]="slice.dash" [attr.stroke-dashoffset]="slice.offset"
                                (mouseenter)="active = slice.key"></circle>
                        }
                    </svg>
                    <div class="dxc-donut__center" aria-hidden="true">
                        <span class="dxc-donut__total" [dxcCountUp]="focus ? focus.count : total" [countUpDuration]="focus ? 240 : 900"></span>
                        <span class="dxc-donut__caption">{{ focus ? focus.label : totalLabel }}</span>
                    </div>
                </div>
                <ul class="dxc-donut__legend">
                    @for (slice of drawn; track slice.key) {
                        <li class="dxc-donut__row" [class.is-active]="active === slice.key" (mouseenter)="active = slice.key">
                            <span class="dxc-donut__name">
                                <span class="dxc-donut__dot dxc-tone--{{ slice.tone }}"></span>
                                <span>{{ slice.label }}</span>
                            </span>
                            <span class="dxc-donut__count">{{ slice.count }}<span class="dxc-donut__pct">{{ slice.pct }}%</span></span>
                        </li>
                    }
                </ul>
            </div>
        }
    `,
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DxcDonutComponent {

    @Input() emptyText = 'Sin datos en este periodo';
    @Input() ariaLabel = '';
    /** Lo que cuenta el total del centro: «fletes», «pólizas». */
    @Input() totalLabel = 'Total';

    readonly radius = RADIUS;
    total = 0;
    drawn: DrawnSlice[] = [];
    /** Categoría apuntada, en la dona o en la leyenda. */
    active: string | null = null;

    @Input() set slices(slices: DxcDonutSlice[]) {
        const visible = (slices ?? []).filter(slice => slice.count > 0);
        this.total = visible.reduce((sum, slice) => sum + slice.count, 0);
        const gap = visible.length > 1 ? GAP : 0;

        let offset = 0;
        this.drawn = visible.map(slice => {
            const length = (slice.count / this.total) * CIRCUMFERENCE;
            // Una porción mínima sigue viéndose aunque el aire se la coma casi entera.
            const stroke = Math.max(1, length - gap);
            const drawn = { ...slice, dash: `${stroke} ${CIRCUMFERENCE - stroke}`, offset: -offset, pct: Math.round((slice.count / this.total) * 100) };
            offset += length;
            return drawn;
        });
        if (this.active && !this.drawn.some(slice => slice.key === this.active)) this.active = null;
    }

    get focus(): DrawnSlice | null {
        return this.active ? this.drawn.find(slice => slice.key === this.active) ?? null : null;
    }
}
